class QuickAdd {
  constructor() {
    this.modal = null;
    this.currentProduct = null;
    this.currentVariant = null;
    this.isProcessing = false;
    this.selectedOptions = [];
    this.translations = window.quickAddTranslations || {
      addToCart: 'Add to Cart',
      soldOut: 'Sold Out',
      adding: 'Adding...'
    };
    this.init();
  }

  init() {
    this.createModal();
    this.attachEventListeners();
  }

  createModal() {
    const modalHTML = `
      <div class="quick-add-modal" id="quickAddModal">
        <div class="quick-add-overlay"></div>
        <div class="quick-add-content">
          <button type="button" class="quick-add-close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="quick-add-body">
            <div class="quick-add-loading">
              <div class="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('quickAddModal');
  }

  attachEventListeners() {
    // Delegate quick-add button clicks
    document.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('.btn-cart');
      if (quickAddBtn) {
        const form = quickAddBtn.closest('form');
        if (form && form.dataset.productId) {
          e.preventDefault();
          e.stopPropagation();
          this.handleQuickAdd(quickAddBtn);
        }
      }

      // Close modal
      if (e.target.closest('.quick-add-close') || e.target.classList.contains('quick-add-overlay')) {
        this.closeModal();
      }
    });

    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  async handleQuickAdd(button) {
    if (this.isProcessing) return;

    const form = button.closest('form');
    if (!form) return;

    const productId = form.dataset.productId;
    const variantId = form.querySelector('input[name="id"]')?.value;

    if (!productId) {
      console.error('Product ID not found');
      return;
    }

    this.isProcessing = true;

    try {
      // Get current locale from HTML lang attribute or Shopify routes
      const locale = document.documentElement.lang || 'de';
      const localePrefix = locale === 'en' ? '/en' : '';
      
      // Fetch product data with retry logic
      let response;
      let retries = 2;
      
      while (retries >= 0) {
        try {
          const productUrl = `${localePrefix}/products/${productId}.js`;
          console.log('Fetching product from:', productUrl);
          response = await fetch(productUrl);
          if (response.ok) break;
          retries--;
          if (retries >= 0) await new Promise(resolve => setTimeout(resolve, 300));
        } catch (fetchError) {
          console.error('Fetch attempt failed:', fetchError);
          retries--;
          if (retries < 0) throw fetchError;
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      if (!response || !response.ok) {
        throw new Error(`Failed to fetch product: ${response?.status || 'Network error'}`);
      }
      
      const product = await response.json();
      
      if (!product || !product.variants) {
        throw new Error('Invalid product data received');
      }
      
      this.currentProduct = product;

      // Check if product has multiple available variants
      const availableVariants = product.variants.filter(v => v.available);

      if (availableVariants.length === 0) {
        alert('Product is out of stock');
        this.isProcessing = false;
        return;
      }

      // Single variant product - add directly
      const isDefaultVariant = product.options.length === 1 && 
        (product.options[0] === 'Title' || product.options[0].name === 'Title');
      
      if (availableVariants.length === 1 || isDefaultVariant) {
        // Single variant - add directly
        await this.addToCart(availableVariants[0].id);
      } else {
        // Multiple variants - show modal
        this.showModal(product);
      }
    } catch (error) {
      console.error('Quick add error:', error);
      console.error('Product ID:', productId);
      console.error('Form:', form);
      
      // More user-friendly error message
      const errorMsg = error.message.includes('Failed to fetch') 
        ? 'Could not load product. Please try again.' 
        : 'Failed to load product';
      alert(errorMsg);
    } finally {
      this.isProcessing = false;
    }
  }

  showModal(product) {
    const modalBody = this.modal.querySelector('.quick-add-body');
    modalBody.innerHTML = this.buildModalContent(product);
    
    this.modal.classList.add('active');
    document.body.classList.add('quick-add-open');

    // Initialize variant selectors
    this.initializeVariantSelectors(product);
  }

  buildModalContent(product) {
    const currentVariant = product.variants.find(v => v.available) || product.variants[0];
    
    return `
      <div class="quick-add-product">
        <div class="quick-add-image">
          <img src="${this.getImageUrl(currentVariant.featured_image?.src || product.featured_image, '400x400')}" 
               alt="${product.title}" 
               id="quickAddImage">
        </div>
        <div class="quick-add-details">
          <h2 class="quick-add-title">${product.title}</h2>
          
          <div class="quick-add-price">
            <span class="price-current" id="quickAddPrice">
              ${this.formatMoney(currentVariant.price)}
            </span>
            ${currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price ? `
              <s class="price-compare" id="quickAddComparePrice">
                ${this.formatMoney(currentVariant.compare_at_price)}
              </s>
            ` : ''}
          </div>

          ${this.buildVariantSelectors(product)}

          <div class="quick-add-actions">
            <button type="button" 
                    class="btn btn-primary quick-add-submit" 
                    id="quickAddSubmit"
                    ${!currentVariant.available ? 'disabled' : ''}>
              <span>${currentVariant.available ? this.translations.addToCart : this.translations.soldOut}</span>
            </button>
          </div>

          ${product.description ? `
            <div class="quick-add-description">
              ${product.description.substring(0, 200)}${product.description.length > 200 ? '...' : ''}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  buildVariantSelectors(product) {
    if (product.options.length === 1 && product.options[0].name === 'Title') {
      return '';
    }

    let html = '<div class="quick-add-variants">';

    product.options.forEach((option, index) => {
      const values = [...new Set(product.variants.map(v => v.options[index]))];
      const optionName = option.name || option;
      
      html += `
        <div class="quick-add-option">
          <div class="swatch clearfix ${optionName}" data-option-index="${index}" style="${optionName === 'Material' ? 'display:none' : ''}">
            ${optionName === 'Material' ? '<div class="header">Variante wählen <a href="#" title="Variante wählen"><img src="' + this.getAssetUrl('i-icon.svg') + '" width="16" height="16"></a></div>' : ''}
            ${values.map(value => {
              const variant = product.variants.find(v => v.options[index] === value && v.available);
              const isAvailable = variant !== undefined;
              
              return `
                <div data-value="${this.escapeHtml(value)}" class="swatch-element ${!isAvailable ? 'soldout' : 'available'}">
                  <input id="quick-swatch-${index}-${this.handleize(value)}" 
                         type="radio" 
                         name="quick-option-${index}" 
                         value="${this.escapeHtml(value)}" 
                         ${!isAvailable ? 'disabled' : ''} />
                  ${this.buildSwatchLabel(optionName, value, index)}
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    });

    html += '</div>';
    return html;
  }

  buildSwatchLabel(optionName, value, index) {
    if (optionName === 'Material' || optionName === 'material') {
      let imageSrc = '';
      const valueLower = value.toLowerCase();
      
      if (valueLower.includes('holz') || valueLower.includes('wooden') || valueLower.includes('wood') || valueLower.includes('lid')) {
        imageSrc = this.getAssetUrl('Holzdeckeln-trans.png');
      } else if (valueLower.includes('kork') || valueLower.includes('cork')) {
        imageSrc = this.getAssetUrl('kork-trans.png');
      } else if (valueLower.includes('pipette') || valueLower.includes('pipet')) {
        imageSrc = this.getAssetUrl('pipette-tran.png');
      } else {
        imageSrc = this.getAssetUrl('pipette-tran.png');
      }
      
      return `
        <label for="quick-swatch-${index}-${this.handleize(value)}">
          <img src="${imageSrc}" alt="${this.escapeHtml(value)}">
          <span>${value}</span>
        </label>
      `;
    }
    
    return `<label for="quick-swatch-${index}-${this.handleize(value)}">${value}</label>`;
  }

  initializeVariantSelectors(product) {
    const swatches = this.modal.querySelectorAll('.swatch input[type="radio"]');
    const submitBtn = this.modal.querySelector('#quickAddSubmit');

    // Initialize selected options array
    this.selectedOptions = [];
    product.options.forEach(() => this.selectedOptions.push(null));

    // Select first available option for each position
    product.options.forEach((option, index) => {
      const firstAvailable = this.modal.querySelector(`.swatch[data-option-index="${index}"] input:not([disabled])`);
      if (firstAvailable) {
        firstAvailable.checked = true;
        this.selectedOptions[index] = firstAvailable.value;
      }
    });

    // Update on selection
    swatches.forEach(swatch => {
      swatch.addEventListener('change', () => {
        this.updateQuickAddVariant(product);
      });
    });

    // Add to cart handler
    submitBtn.addEventListener('click', () => {
      this.handleQuickAddSubmit(product);
    });

    // Initial update
    this.updateQuickAddVariant(product);
  }

  updateQuickAddVariant(product) {
    // Get selected options
    product.options.forEach((option, index) => {
      const selected = this.modal.querySelector(`.swatch[data-option-index="${index}"] input:checked`);
      if (selected) {
        this.selectedOptions[index] = selected.value;
      }
    });

    // Handle Material option visibility (hide for small sizes)
    const firstOption = this.selectedOptions[0];
    const materialSwatch = this.modal.querySelector('.swatch.Material');
    if (materialSwatch && firstOption) {
      const smallSizes = ['2ml', '5ml', '10ml', '4ml', 'Mini'];
      if (smallSizes.includes(firstOption)) {
        materialSwatch.style.display = 'none';
      } else {
        materialSwatch.style.display = 'flex';
      }
    }

    // Update available options for each position based on current selection
    this.updateAvailableOptions(product);

    // Find matching variant
    const variant = product.variants.find(v => 
      v.options.every((opt, idx) => opt === this.selectedOptions[idx])
    );

    if (!variant) return;

    // Update image
    const image = this.modal.querySelector('#quickAddImage');
    if (variant.featured_image) {
      image.src = this.getImageUrl(variant.featured_image.src, '400x400');
    }

    // Update price
    const priceEl = this.modal.querySelector('#quickAddPrice');
    priceEl.textContent = this.formatMoney(variant.price);

    // Update compare price
    const comparePriceEl = this.modal.querySelector('#quickAddComparePrice');
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (comparePriceEl) {
        comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = 'inline';
      } else {
        priceEl.insertAdjacentHTML('afterend', `
          <s class="price-compare" id="quickAddComparePrice">
            ${this.formatMoney(variant.compare_at_price)}
          </s>
        `);
      }
    } else if (comparePriceEl) {
      comparePriceEl.style.display = 'none';
    }

    // Update button
    const submitBtn = this.modal.querySelector('#quickAddSubmit');
    if (variant.available) {
      submitBtn.disabled = false;
      submitBtn.querySelector('span').textContent = this.translations.addToCart;
    } else {
      submitBtn.disabled = true;
      submitBtn.querySelector('span').textContent = this.translations.soldOut;
    }

    // Store current variant
    this.currentVariant = variant;
  }

  async handleQuickAddSubmit(product) {
    if (!this.currentVariant || !this.currentVariant.available || this.isProcessing) {
      return;
    }

    await this.addToCart(this.currentVariant.id);
  }

  async addToCart(variantId) {
    const submitBtn = this.modal?.querySelector('#quickAddSubmit');
    
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.querySelector('span').textContent = this.translations.adding;
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

      // Refresh cart via CartDrawer class
      if (window.cartDrawer) {
        await window.cartDrawer.refreshCart();
        window.cartDrawer.openDrawer();
      }

      this.closeModal();
    } catch (error) {
      console.error('Add to cart error:', error);
      alert(error.message || 'Failed to add item to cart');
      
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.querySelector('span').textContent = this.translations.addToCart;
      }
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.classList.remove('active');
      document.body.classList.remove('quick-add-open');
      
      // Clear content after animation
      setTimeout(() => {
        const modalBody = this.modal.querySelector('.quick-add-body');
        modalBody.innerHTML = '<div class="quick-add-loading"><div class="spinner"></div></div>';
      }, 300);
    }
  }

  updateAvailableOptions(product) {
    product.options.forEach((option, optionIndex) => {
      const swatch = this.modal.querySelector(`.swatch[data-option-index="${optionIndex}"]`);
      if (!swatch) return;

      const swatchElements = swatch.querySelectorAll('.swatch-element');
      const optionName = product.options[optionIndex].name || product.options[optionIndex];
      const isMaterialOption = optionName === 'Material';
      
      swatchElements.forEach(element => {
        const input = element.querySelector('input');
        const value = input.value;
        
        // Check if this option value is available with current selections
        const testOptions = [...this.selectedOptions];
        testOptions[optionIndex] = value;
        
        const hasAvailableVariant = product.variants.some(variant => {
          let matches = true;
          testOptions.forEach((opt, idx) => {
            if (opt && variant.options[idx] !== opt) {
              matches = false;
            }
          });
          return matches && variant.available;
        });
        
        if (hasAvailableVariant) {
          element.classList.remove('soldout');
          element.classList.add('available');
          element.style.display = '';
          input.disabled = false;
        } else {
          if (isMaterialOption) {
            element.style.display = 'none';
          } else {
            element.classList.add('soldout');
            element.classList.remove('available');
            element.style.display = '';
          }
          input.disabled = true;
          
          // If this was selected, uncheck it
          if (input.checked) {
            input.checked = false;
            this.selectedOptions[optionIndex] = null;
            
            // Select first available option
            const firstAvailable = swatch.querySelector('input:not([disabled])');
            if (firstAvailable) {
              firstAvailable.checked = true;
              this.selectedOptions[optionIndex] = firstAvailable.value;
            }
          }
        }
      });
    });
  }

  getAssetUrl(filename) {
    // Remove locale prefix from root path
    let root = '/';
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root.replace(/^\/(en|de)\//, '/');
    }
    return `${root}cdn/shop/t/44/assets/${filename}`;
  }

  handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getImageUrl(src, size) {
    if (!src) return '';
    return src.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '{{amount}}');
    }
    return `€${(cents / 100).toFixed(2)}`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.quickAdd = new QuickAdd();
  });
} else {
  window.quickAdd = new QuickAdd();
}
